import qs from 'qs';
import { api } from 'src/api/api';
import { TimelineRecord } from 'src/types/convocation-summary';

import type { TimelineAnalysis, TimelineAnalysisResponse } from '../types';

export type TimelineFilterOptions = {
  markets: string[];
  categories: string[];
  types: string[];
  species: string[];
  themes: string[];
  relevances: string[];
};

export type TimelineSummary = {
  records: number;
  analyzed: number;
  highMateriality: number;
  citations: number;
};

export type TimelineSortRule = {
  id: 'materiality' | 'datareferencia';
  desc: boolean;
};

export type FetchTimelineDataParams = {
  page: number;
  pageSize: number;
  operationId?: string;
  signal?: AbortSignal;
  search?: string;
  markets?: string[];
  categories?: string[];
  types?: string[];
  species?: string[];
  themes?: string[];
  relevances?: string[];
  dateStart?: string;
  dateEnd?: string;
  favorites?: boolean;
  sort?: TimelineSortRule[];
};

type TimelineApiRecord = {
  id: number;
  documentId?: number | string | null;
  description?: string | null;
  secondarySubjects?: string | null;
  favorite?: { isFavorite: boolean; id: number | null } | null;
  category?: string | null;
  type?: string | null;
  species?: string | null;
  theme?: string | null;
  eventType?: string | null;
  eventGroup?: string | null;
  family?: string | null;
  relevance?: string | null;
  date?: string | null;
  deliveryDate?: string | null;
  summary?: string | null;
  citationsTotal?: number | null;
  pagesRead?: number | null;
  operation?: {
    id?: number | null;
    hashId?: string | null;
    name?: string | null;
    slug?: string | null;
    cnpj?: string | null;
  } | null;
  market?: { name?: string | null } | null;
};

type TimelineApiResponse = {
  data: TimelineApiRecord[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
};

type TimelineFilterOptionsResponse = {
  data: TimelineFilterOptions;
};

const toTimelineRecord = (record: TimelineApiRecord): TimelineRecord => ({
  id: record.id,
  documentId:
    record.documentId === undefined || record.documentId === null
      ? null
      : String(record.documentId),
  descricaofundo: record.description,
  assuntosSecundarios: record.secondarySubjects,
  operationNumericId: record.operation?.id,
  favorite: record.favorite,
  categoriadocumento: record.category,
  tipodocumento: record.type,
  especiedocumento: record.species,
  assunto: record.theme,
  eventoTipo: record.eventType,
  eventoGrupo: record.eventGroup,
  familia: record.family,
  relevancia: record.relevance,
  datareferencia: record.date,
  dataentrega: record.deliveryDate,
  resumo: record.summary,
  citacoesTotal:
    record.citationsTotal === undefined || record.citationsTotal === null
      ? null
      : String(record.citationsTotal),
  paginasLidas:
    record.pagesRead === undefined || record.pagesRead === null
      ? null
      : String(record.pagesRead),
  operationid: record.operation?.hashId,
  operationName: record.operation?.name,
  operationSlug: record.operation?.slug,
  cnpj: record.operation?.cnpj,
  mercado: record.market?.name,
});

const toQueryParams = (params: FetchTimelineDataParams) => {
  const queryParams: Record<string, unknown> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (key === 'signal') return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === 'string' && !value.trim()) return;
    if (value === undefined || value === false) return;

    queryParams[key] =
      key === 'search' && typeof value === 'string' ? value.trim() : value;
  });

  return queryParams;
};

export const fetchTimelineData = async (params: FetchTimelineDataParams) => {
  const query = qs.stringify(toQueryParams(params), {
    encodeValuesOnly: true,
  });
  const endpoint = `/meta-linha-do-tempos/timeline?${query}`;
  const { data: response } = params.signal
    ? await api.get<TimelineApiResponse>(endpoint, { signal: params.signal })
    : await api.get<TimelineApiResponse>(endpoint);

  return {
    ...response,
    data: (response.data ?? []).map(toTimelineRecord),
  };
};

export const getTimelineFilterOptions = async (
  signal?: AbortSignal,
  operationId?: string,
) => {
  const query = qs.stringify(operationId ? { operationId } : {}, {
    encodeValuesOnly: true,
  });
  const endpoint = `/meta-linha-do-tempos/filters${query ? `?${query}` : ''}`;
  const { data: response } = signal
    ? await api.get<TimelineFilterOptionsResponse>(endpoint, { signal })
    : await api.get<TimelineFilterOptionsResponse>(endpoint);

  return response.data;
};

export const fetchTimelineAnalysis = async (
  id: number,
  signal?: AbortSignal,
): Promise<TimelineAnalysis> => {
  const endpoint = '/meta-linha-do-tempos/' + id + '/analysis';
  const { data: response } = signal
    ? await api.get<TimelineAnalysisResponse>(endpoint, { signal })
    : await api.get<TimelineAnalysisResponse>(endpoint);

  return response.data;
};

export const getTimelineSummary = async (
  signal?: AbortSignal,
  operationId?: string,
) => {
  const query = qs.stringify(operationId ? { operationId } : {}, {
    encodeValuesOnly: true,
  });
  const endpoint = `/meta-linha-do-tempos/summary${query ? `?${query}` : ''}`;
  const { data: response } = signal
    ? await api.get<TimelineSummary>(endpoint, {
        signal,
      })
    : await api.get<TimelineSummary>(endpoint);

  return response;
};
